import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { GlobalLoading } from './contexts/GlobalLoading';
import { SnackbarProvider } from './contexts/SnackbarProvider';
import { LogoProvider } from './contexts/LogoContext';
import RequireAuth from './components/RequireAuth';
import axiosInstance from './utils/axiosInstance';
import ScrollToTop from './utils/ScrollToTop';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import TopPage from './pages/TopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CategoryPage from './pages/CategoryPage';
import ProductsPage from './pages/ProductsPage';
import EcCartPage from './pages/EcCartPage';
import EcCartCheckoutPage from './pages/EcCartCheckoutPage';
import EcCartCompletePage from './pages/EcCartCompletePage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ClientNewPage from './pages/ClientNewPage';
import ClientNewCompletePage from './pages/ClientNewCompletePage';
import ContactInputPage from './pages/ContactInputPage';
import ContactCompletePage from './pages/ContactCompletePage';
import CompanyPage from './pages/CompanyPage';
import SetPasswordPage from './pages/SetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

function App() {
  const [isAuth, setIsAuth] = useState(false);
  const [tenantLimits, setTenantLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('ゲスト');
  const location = useLocation();
  const navigate = useNavigate();

  const isPublicPage = [
    '/login',
    '/set-password',
    '/forgot-password',
    '/not-found',
  ].some(path => location.pathname.startsWith(path));

  useEffect(() => {
    const init = async () => {
      try {
        // テナント制限取得
        const limitsRes = await axiosInstance.get('/tenant/limits');
        setTenantLimits(limitsRes.data);

        // 認証チェック（公開ページ以外）
        if (!isPublicPage) {
          const userRes = await axiosInstance.get('/login/check-login');
          setUserName(userRes.data.user.username);
          setIsAuth(true);
        }
      } catch {
        setIsAuth(false);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [location.pathname]);

  const hasLimit = (key) =>
    tenantLimits.some(l => l.key_name === key && Number(l.option_value) > 0);

  const login = () => {
    localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    navigate('/login');
  };
  const logout = async () => {
    try { await axiosInstance.post(`/auth/logout`); } catch { } finally {
      setIsAuth(false);
      setUserName('ゲスト');
      navigate('/', { replace: true });
    }
  };

  // --- ロード中は GlobalLoading 表示 ---
  if (loading) return <GlobalLoading />;

  // EC権限チェック
  if (!hasLimit('ec') && location.pathname !== '/not-found') {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ScrollToTop />
      <GlobalLoading>
        <SnackbarProvider>
          <LogoProvider>
            {isPublicPage ? (
              <>
                {location.pathname.startsWith('/login') && (
                  <LoginPage
                    setUserName={setUserName}
                    setIsAuth={setIsAuth}
                  />
                )}
                {location.pathname.startsWith('/set-password') && <SetPasswordPage />}
                {location.pathname.startsWith('/forgot-password') && <ForgotPasswordPage />}
                {location.pathname.startsWith('/not-found') && <NotFoundPage />}
              </>
            ) : (
              <Routes>
                <Route
                  element={<Layout userName={userName} isAuth={isAuth} logout={logout} login={login} theme={theme} />}
                >
                  {/* 公開ページ（ログイン不要） */}
                  <Route path="/" element={<TopPage />} />
                  <Route path="/category/:category_id" element={<CategoryPage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/:id" element={<ProductDetailPage login={login} />} />
                  <Route path="/contact" element={<ContactInputPage />} />
                  <Route path="/contact/complete" element={<ContactCompletePage />} />
                  <Route path="/clients/new" element={<ClientNewPage />} />
                  <Route path="/clients/new/complete" element={<ClientNewCompletePage />} />
                  <Route path="/company" element={<CompanyPage />} />

                  {/* 認証必須ページ */}
                  <Route path="/dashboard" element={<RequireAuth isAuth={isAuth}><TopPage /></RequireAuth>} />
                  <Route path="/cart" element={<RequireAuth isAuth={isAuth}><EcCartPage /></RequireAuth>} />
                  <Route path="/cart/checkout" element={<RequireAuth isAuth={isAuth}><EcCartCheckoutPage /></RequireAuth>} />
                  <Route path="/cart/complete" element={<RequireAuth isAuth={isAuth}><EcCartCompletePage /></RequireAuth>} />
                  <Route path="/orders" element={<RequireAuth isAuth={isAuth}><OrdersPage /></RequireAuth>} />
                  <Route path="/orders/:id" element={<RequireAuth isAuth={isAuth}><OrderDetailPage /></RequireAuth>} />
                  <Route path="/clients/detail" element={<RequireAuth isAuth={isAuth}><ClientDetailPage logout={logout} /></RequireAuth>} />
                </Route>

                {/* NotFound */}
                <Route path="/not-found" element={<NotFoundPage />} />
                <Route path="*" element={<Navigate to="/not-found" replace />} />
              </Routes>
            )}
          </LogoProvider>
        </SnackbarProvider>
      </GlobalLoading>
    </ThemeProvider>
  );
}

export default App;
